import { useState } from 'react';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/solid';
import { NavLink } from "react-router-dom";

const faqs = [
  {
    question: "What is the NDIS?",
    answer: "The National Disability Insurance Scheme (NDIS) is a government initiative that provides support to Australians with a permanent and significant disability, as well as their families and carers. It aims to help people with disabilities live more independently and achieve personal goals.",
  },
  {
    question: "Who is eligible for the NDIS?",
    answer: "To be eligible for the NDIS, you must be an Australian citizen, permanent resident, or hold a Protected Special Category Visa. You must also have a permanent disability that significantly impacts your ability to take part in everyday activities.",
  },
  {
    question: "How do I apply for the NDIS?",
    answer: "You can apply for the NDIS by contacting the National Disability Insurance Agency (NDIA) directly. They will guide you through the application process, which includes providing evidence of your disability and how it impacts your life.",
  },
  {
    question: "What types of support does the NDIS provide?",
    answer: "The NDIS provides a wide range of supports, including help with daily personal activities, transport, community participation, therapeutic support, and more. These supports are designed to help you achieve your goals related to education, employment, independence, or community involvement.",
  },
   {
    question: "How are NDIS plans created?",
    answer: "NDIS plans are created during a planning meeting with an NDIS representative. During this meeting, you’ll discuss your goals, needs, and the types of support that will help you achieve them. Your plan is then tailored to provide the appropriate funding for these supports.",
  },
   {
    question: "Can I choose my own service providers?",
    answer: "Yes, the NDIS gives participants the choice and control to select their own service providers. This means you can choose the providers that best meet your needs and preferences, ensuring you receive the right support for your goals.",
  },
  {
    question: "What happens if my needs change?",
    answer: "If your needs change, you can request a plan review. This review will consider any new or different supports you require. It’s important to keep your plan up-to-date so that it continues to meet your needs effectively.",
  },
  {
    question: "How do I manage my NDIS funding?",
    answer: "You can manage your NDIS funding in several ways: self-management, plan management, or NDIA management. Each option offers different levels of flexibility and control over how you use your funds.",
  },
   {
    question: "What is a Local Area Coordinator (LAC)?",
    answer: "A Local Area Coordinator (LAC) works with the NDIA to help people understand and access the NDIS. They can help you plan and connect with local services and supports that align with your goals.",
  },
   {
    question: "How can Abha Care help me with my NDIS plan?",
    answer: "At ABHA Care, we provide personalised support to help you navigate the NDIS. From understanding your plan to accessing the right services, our team is here to guide you every step of the way.",
  }
  // Add more as needed...
];

const NDISResources = () => {
  return (
    <main>
      
      {/* Header className="px-4 sm:px-6 lg:px-16 py-12 space-y-16" Section */}
      <section className="py-16 px-4 md:px-10 lg:px-16">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Resources</h2>


       <div className="flex flex-col md:flex-row items-start md:items-stretch gap-4">
  {/* Left image */}
  <img
    src="/images/Resources1.jpg"
    alt="Resources"
    className="w-full md:w-1/2 rounded-xl object-cover h-auto md:h-auto"
  />

  {/* Right image with scrolling text overlay */}
  <div className="relative w-full md:w-1/2 rounded-xl overflow-hidden h-auto">
    <img
      src="/images/Resources2.jpg"
      alt="Resources Text"
      className="w-full h-full object-cover"
    />

    {/* Scrolling text overlay */}
    <div className="absolute inset-4 overflow-hidden">
      <div className="animate-marquee whitespace-normal text-white font-semibold py-80 text-sm sm:text-base md:text-lg">
        <h4 className="font-bold mb-2">NDIS FAQs and Resources</h4>
        As complicated as the National Disability Insurance Scheme (NDIS) can be, ABHA Care Pty Ltd can help you 
        understand the process and make the most of your NDIS plan. We’ve compiled answers to some of the most 
        frequently asked questions about the NDIS and helpful resources to guide you on your journey.
      </div>
    </div>
  </div>
</div>
</section>

      {/* Videos Section */}
      <section className="py-16 px-4 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              title: "How To - What is the NDIS?",
              src: "https://www.youtube.com/embed/qZOjPBJiBPg",
            },
            {
              title: "How To - Manage Your NDIS Plan - Overview",
              src: "https://www.youtube.com/embed/0KT3g3sf6-I",
            },
            {
              title: "Communicating with your provider",
              src: "https://www.youtube.com/embed/EKuraO6yaWk",
            },
          ].map((video, i) => (
            <div key={i} className="h-[400px] flex flex-col">
              <div className="aspect-w-16 aspect-h-9">
                <iframe
                  src={video.src}
                  title={video.title}
                  allowFullScreen
                  className="rounded-md w-full h-[350px]"
                />
              </div>
              <p className="mt-2 text-lg font-semibold text-gray-800">{video.title}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Guides and Checklists */}
      <section className="py-16 px-4 md:px-10 lg:px-16">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Guides and Checklists</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: "https://abhacare.pdstaging.com.au/wp-content/uploads/2024/09/Group-26.svg",
              title: "Checklist: Discover If You Qualify For NDIS",
              desc: "Unlock the possibilities of personalised care through our NDIS checklist.",
              url: "https://www.ndis.gov.au/print/pdf/node/385",
            },
            {
              icon: "https://abhacare.pdstaging.com.au/wp-content/uploads/2024/09/Group-27.svg",
              title: "Guide 1: How To Apply For Support",
              desc: "Navigate the application journey confidently with our step-by-step guide.",
              url: "https://www.ndis.gov.au/media/79/download?attachment",
            },
            {
              icon: "https://abhacare.pdstaging.com.au/wp-content/uploads/2024/09/Group-1.png",
              title: "Guide 2: How To Craft Your NDIS Plan",
              desc: "Participate in creating your care plan through our comprehensive guide.",
              url: "https://www.ndis.gov.au/media/334/download?attachment",
            },
          ].map((guide, i) => (
            <div key={i} className="bg-white border rounded-lg p-6 min-h-[300px] shadow-sm text-center space-y-4">
              <img src={guide.icon} alt="" className="mx-auto w-10" />
              <h3 className="font-bold text-lg text-gray-800">{guide.title}</h3>
              <p className="text-gray-600">{guide.desc}</p>
              <a
                href={guide.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600[#6f881e] font-semibold border-b border-blue-600 hover:text-blue-800"
              >
                Download Now
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 px-4 md:px-10 lg:px-16">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <Disclosure key={i}>
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex justify-between w-full px-4 py-2 text-left text-md font-medium text-gray-800 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none">
                    <span>{faq.question}</span>
                    <ChevronUpIcon
                      className={`${
                        open ? 'transform rotate-180' : ''
                      } w-5 h-5 text-gray-600`}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-4 pt-2 pb-4 text-gray-600">
                    {faq.answer}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          ))}
        </div>
      </section>

       {/*Info Section */}
          <section className="bg-[#3f4f2b] text-white mb-0 py-16 px-4 md:px-10 lg:px-16">
            <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-10 text-sm">
              {/* Logo + Location */}
              <div className="flex flex-col items-center md:items-start text-center md:text-left space-y-4">
                <img src="/images/Logo-inverted.svg" alt="ABHA CARE Logo" className="h-16 mx-auto md:mx-0" />
                <p className="font-semibold">New South Wales, Australia</p>
              </div>
          
              {/* Site Links */}
              <div>
                <h4 className="font-bold mb-4 text-lg">Site</h4>
                <ul className="space-y-2 text-lg">
                  <li><NavLink to="/" className="hover:underline">Home</NavLink></li>
                  <li><NavLink to="/about" className="hover:underline">About Us</NavLink></li>
                  <li><NavLink to="/services" className="hover:underline">Services</NavLink></li>
                  <li><NavLink to="/faq" className="hover:underline">FAQ</NavLink></li>
                  <li><NavLink to="/contact" className="hover:underline">Contact Us</NavLink></li>
                </ul>
              </div>
          
              {/* Location + Hours */}
              <div>
                <h4 className="font-bold mb-4 text-lg">Location</h4>
                <p className="text-lg">Abha Care Pty Ltd<br />
                <br /></p>
          
                <h4 className="font-bold mt-6 mb-2 text-lg">Office Hours</h4>
                <p className="text-lg">
                      Monday - Friday: 9:00AM - 5:00PM<br />
                      Saturday & Sunday: Closed</p>
              </div>
          
              {/* Contact Info */}
              <div>
                <h4 className="font-bold mb-4 text-lg">Contact</h4>
                <p className="text-lg">Phone: 02 8395 2893<br /></p>
                <p className="text-lg  ml-14">1300 157 211<br /></p>
                <p className="mt-2 text-lg">
                <a href="mailto:info@abhacare.com.au" className="hover:underline text-lg">
                  info@abhacare.com.au
                </a>
              </p>
              </div>
            </div>
          </section>
    </main>
  );
}
export default NDISResources;
